import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, resolveComponent as _resolveComponent, createBlock as _createBlock, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, withModifiers as _withModifiers, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "expand-icon" }
const _hoisted_2 = { class: "group-label" }
const _hoisted_3 = {
  key: 0,
  class: "group-date"
}
const _hoisted_4 = { class: "group-duration" }
const _hoisted_5 = { class: "group-amount" }
const _hoisted_6 = { class: "group-count" }
const _hoisted_7 = {
  key: 0,
  class: "group-content"
}
const _hoisted_8 = {
  key: 0,
  class: "state-row"
}
const _hoisted_9 = {
  key: 1,
  class: "state-row error-row"
}
const _hoisted_10 = {
  key: 3,
  class: "entries-table"
}
const _hoisted_11 = ["onClick"]
const _hoisted_12 = { class: "entry-date" }
const _hoisted_13 = { class: "entry-user" }
const _hoisted_14 = { class: "entry-project" }
const _hoisted_15 = { class: "entry-theme" }
const _hoisted_16 = { class: "entry-notes" }
const _hoisted_17 = { class: "entry-duration" }
const _hoisted_18 = { class: "entry-amount" }
const _hoisted_19 = {
  key: 4,
  class: "load-more-row"
}

import { computed, ref, watch } from "vue";
import { useApolloClient } from "@vue/apollo-composable";
import dayjs from "dayjs";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";
import { logErrorMessages } from "@vue/apollo-util";

import {
  GET_TIMESHEET_EXPLORER_ENTRIES,
  GET_TIMESHEET_GROUPS,
} from "@/graphql/timesheets/queries";
import { formatDurationMinutes } from "@/helpers/dates";
import { formatPrice } from "@/helpers/utils";
import BasicButton from "@/components/BasicButton.vue";
import timesheetI18n from "@/locales/timesheets/en-UK.json";

type MessageSchema = typeof timesheetI18n;

type TimesheetGroupDimension = "PROJECT" | "USER" | "THEMATIC";

type ExplorerFilters = {
  fromDate?: string | null;
  toDate?: string | null;
  projectIds?: string[] | null;
  userIds?: string[] | null;
  thematicIds?: string[] | null;
  activeOnly: boolean;
};

type TimesheetGroup = {
  key: string;
  id: string | null;
  label: string;
  duration: number;
  count: number;
  latestDate: string | null;
  childType: "GROUP" | "TIMESHEET";
  amount: number;
};

type TimesheetEntry = {
  id: string;
  date: string;
  duration: number;
  notes?: string | null;
  projectId?: string | null;
  projectName?: string | null;
  userId?: string | null;
  userName?: string | null;
  thematicId?: string | null;
  thematicName?: string | null;
  roleId?: string | null;
  roleName?: string | null;
  hourlyRate?: number | null;
  amount: number;
};

const PAGE_SIZE = 20;


export default /*@__PURE__*/_defineComponent({
  __name: 'TimesheetGroupNode',
  props: {
    group: {},
    path: {},
    level: {},
    groupBy: {},
    filters: {},
    projectId: {},
    editInModal: { type: Boolean },
    refreshVersion: {}
  },
  emits: ["open-entry"],
  setup(__props: any, { emit: __emit }) {

const { t } = useI18n<[MessageSchema], "en-UK">({
  useScope: "global",
  inheritLocale: true,
  messages: {
    "en-UK": timesheetI18n,
  },
});

const formatAmount = (amount: number) => formatPrice(amount);

const props = __props;

const emit = __emit;

const { client } = useApolloClient();
const toast = useToast();

const openTimesheetEntry = (entry: TimesheetEntry) => {
  if (!props.editInModal) return;
  emit("open-entry", entry);
};

const expanded = ref(false);
const loading = ref(false);
const error = ref<string | null>(null);
const childGroups = ref<TimesheetGroup[]>([]);
const entries = ref<TimesheetEntry[]>([]);
const offset = ref(0);
const totalItems = ref(0);

let requestVersion = 0;

const clearLoadedData = () => {
  childGroups.value = [];
  entries.value = [];
  offset.value = 0;
  totalItems.value = 0;
  error.value = null;
};

const hasMore = computed(() => {
  const loaded =
    props.group.childType === "GROUP"
      ? childGroups.value.length
      : entries.value.length;

  return loaded < totalItems.value;
});

const loadPage = async (reset = false) => {
  if (!reset && loading.value) return;

  const version = reset ? ++requestVersion : requestVersion;

  loading.value = true;
  error.value = null;

  const nextOffset = reset ? 0 : offset.value;

  if (props.group.childType === "GROUP") {
    const response = await client
      .query({
        query: GET_TIMESHEET_GROUPS,
        variables: {
          projectId: props.projectId ?? null,
          filters: props.filters,
          groupBy: props.groupBy,
          level: props.level + 1,
          parentGroupKeys: props.path,
          offset: nextOffset,
          limit: PAGE_SIZE,
        },
        fetchPolicy: "network-only",
      })
      .catch((e) => {
        if (version === requestVersion) {
          logErrorMessages(e);
          toast.error(t("timesheets.errors.couldNotLoadGroupedTimesheets"));
          error.value =
            e instanceof Error
              ? e.message
              : t("timesheets.errors.couldNotLoadGroupedTimesheets");
          loading.value = false;
        }

        return null;
      });

    if (version !== requestVersion || !response) return;

    const payload = response.data?.timesheetGroups;

    if (payload?.status !== "ok") {
      logErrorMessages(payload?.error);

      toast.error(
        payload?.error || t("timesheets.errors.couldNotLoadGroupedTimesheets"),
      );

      error.value =
        payload?.error || t("timesheets.errors.couldNotLoadGroupedTimesheets");

      loading.value = false;
      return;
    }

    const page = payload.data;

    childGroups.value = reset
      ? page.groups
      : [...childGroups.value, ...page.groups];

    totalItems.value = page.pageInfo.totalItems;
    offset.value = childGroups.value.length;
  } else {
    const response = await client
      .query({
        query: GET_TIMESHEET_EXPLORER_ENTRIES,
        variables: {
          projectId: props.projectId ?? null,
          filters: props.filters,
          parentGroupKeys: props.path,
          offset: nextOffset,
          limit: PAGE_SIZE,
        },
        fetchPolicy: "network-only",
      })
      .catch((e) => {
        if (version === requestVersion) {
          logErrorMessages(e);
          toast.error(t("timesheets.errors.couldNotLoadTimesheets"));

          error.value =
            e instanceof Error
              ? e.message
              : t("timesheets.errors.couldNotLoadTimesheets");

          loading.value = false;
        }

        return null;
      });

    if (version !== requestVersion || !response) return;

    const payload = response.data?.timesheetExplorerEntries;

    if (payload?.status !== "ok") {
      logErrorMessages(payload?.error);

      toast.error(
        payload?.error || t("timesheets.errors.couldNotLoadTimesheets"),
      );

      error.value =
        payload?.error || t("timesheets.errors.couldNotLoadTimesheets");

      loading.value = false;
      return;
    }

    const page = payload.data;

    entries.value = reset ? page.entries : [...entries.value, ...page.entries];

    totalItems.value = page.pageInfo.totalItems;
    offset.value = entries.value.length;
  }

  loading.value = false;
};

const toggle = async () => {
  expanded.value = !expanded.value;

  if (
    expanded.value &&
    childGroups.value.length === 0 &&
    entries.value.length === 0
  ) {
    await loadPage(true);
  }
};

watch(
  [() => props.filters, () => props.groupBy, () => props.refreshVersion],
  async () => {
    ++requestVersion;
    clearLoadedData();

    if (expanded.value) {
      await loadPage(true);
    }
  },
  { deep: true },
);

return (_ctx: any,_cache: any) => {
  const _component_TimesheetGroupNode = _resolveComponent("TimesheetGroupNode", true)!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["timesheet-group-node", {
      'timesheet-group-node--expanded': expanded.value,
      'timesheet-group-node--nested': __props.level > 0,
    }])
  }, [
    _createElementVNode("button", {
      class: "group-header",
      type: "button",
      onClick: toggle
    }, [
      _createElementVNode("span", _hoisted_1, _toDisplayString(expanded.value ? "▾" : "▸"), 1),
      _createElementVNode("span", _hoisted_2, _toDisplayString(__props.group.label), 1),
      (__props.group.latestDate)
        ? (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(_unref(dayjs)(__props.group.latestDate).format("DD/MM/YYYY")), 1))
        : _createCommentVNode("", true),
      _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(formatDurationMinutes)(__props.group.duration)), 1),
      _createElementVNode("span", _hoisted_5, _toDisplayString(formatAmount(__props.group.amount)), 1),
      _createElementVNode("span", _hoisted_6, _toDisplayString(_unref(t)("timesheets.entries", { count: __props.group.count })), 1)
    ]),
    (expanded.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
          (loading.value && childGroups.value.length === 0 && entries.value.length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(_unref(t)("timesheets.loading")), 1))
            : (error.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_9, _toDisplayString(error.value), 1))
              : _createCommentVNode("", true),
          (__props.group.childType === 'GROUP')
            ? (_openBlock(true), _createElementBlock(_Fragment, { key: 2 }, _renderList(childGroups.value, (child) => {
                return (_openBlock(), _createBlock(_component_TimesheetGroupNode, {
                  key: `${__props.path.join('/')}/${child.key}`,
                  group: child,
                  path: [...__props.path, child.key],
                  level: __props.level + 1,
                  "group-by": __props.groupBy,
                  filters: __props.filters,
                  "project-id": __props.projectId,
                  "edit-in-modal": __props.editInModal,
                  "refresh-version": __props.refreshVersion,
                  onOpenEntry: _cache[0] || (_cache[0] = ($event: any) => (emit('open-entry', $event)))
                }, null, 8, ["group", "path", "level", "group-by", "filters", "project-id", "edit-in-modal", "refresh-version"]))
              }), 128))
            : (_openBlock(), _createElementBlock("div", _hoisted_10, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(entries.value, (entry) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: entry.id,
                    class: _normalizeClass(["entry-row", { 'entry-row--clickable': __props.editInModal }]),
                    onClick: ($event: any) => (__props.editInModal && openTimesheetEntry(entry))
                  }, [
                    _createElementVNode("span", _hoisted_12, _toDisplayString(_unref(dayjs)(entry.date).format("DD/MM/YYYY")), 1),
                    _createElementVNode("span", _hoisted_13, _toDisplayString(entry.userName || _unref(t)("timesheets.placeholder.noValue")), 1),
                    _createElementVNode("span", _hoisted_14, _toDisplayString(entry.projectName || _unref(t)("timesheets.placeholder.noValue")), 1),
                    _createElementVNode("span", _hoisted_15, _toDisplayString(entry.thematicName || _unref(t)("timesheets.placeholder.noValue")), 1),
                    _createElementVNode("span", _hoisted_16, _toDisplayString(entry.notes || ""), 1),
                    _createElementVNode("span", _hoisted_17, _toDisplayString(_unref(formatDurationMinutes)(entry.duration)), 1),
                    _createElementVNode("span", _hoisted_18, _toDisplayString(formatAmount(entry.amount)), 1)
                  ], 10, _hoisted_11))
                }), 128))
              ])),
          (hasMore.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                _createVNode(BasicButton, {
                  disabled: loading.value,
                  onClick: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (loadPage(false)), ["stop"]))
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(loading.value ? _unref(t)("timesheets.loading") : _unref(t)("timesheets.loadMore")), 1)
                  ]),
                  _: 1
                }, 8, ["disabled"])
              ]))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})